import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Image, Icon, Table } from "semantic-ui-react";
import { image } from "../../../../assets";
import { Permission, User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { BasicModal, ConfirmDialog } from "../../../Shared";
import { ENV } from "../../../../utils";
import { UserForm } from "../UserForm";
import "./UserItem.scss";
import {
  hasPermission,
  isAdmin,
  isMaster,
} from "../../../../utils/checkPermission";
import { useLanguage } from "../../../../contexts";

const userController = new User();
const permissionController = new Permission();

export function UserItem(props) {
  const { user, onReload, hideActivate = false, showCompanyColumn = false, showSiteColumn = false } = props;
  const {
    accessToken,
    user: { role , sites},
  } = useAuth();

  const [permissionsByRole, setPermissionsByRole] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [titleModal, setTitleModal] = useState("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmHeader, setConfirmHeader] = useState("");
  const [isDelete, setIsDelete] = useState(false);
  const [isActivationError, setIsActivationError] = useState(false);
  const [rowMenu, setRowMenu] = useState(null); // { top, left, openUp } | null

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".user-item__row-menu, .user-item__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (e) => {
    if (rowMenu) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  const { translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  useEffect(() => {
    (async () => {
      try {
        setPermissionsByRole([]);
        if (role) {
          const response = await permissionController.getPermissionsByRole(
            accessToken,
            role._id,
            true
          );
          setPermissionsByRole(response);
        }
      } catch (error) {
        console.error(error);
        setPermissionsByRole([]);
      }
    })();
  }, [role]);

  const openUpdateUser = () => {
    setTitleModal(`${t("update")} ${user.email}`);
    onOpenCloseModal();
  };

  const openDesactivateActivateConfim = () => {
    setIsDelete(false);

    // Validar campos requeridos antes de activar
    if (!user.active) {
      const missing = [];
      if (!user.role) missing.push(t("role"));
      if (!user.company && !(user.sites && user.sites.length)) missing.push(`${t("company")} / ${t("site")}`);

      if (missing.length > 0) {
        setIsActivationError(true);
        setConfirmMessage(`${t("cannot_activate_user_missing_data")} ${missing.join(", ")}.`);
        onOpenCloseConfirm();
        return;
      }
    }

    setIsActivationError(false);
    setConfirmHeader("Activar/Desactivar");
    setConfirmMessage(
      user.active
        ? `Desactivar usuario "${user.email}"`
        : `Activar usuario "${user.email}"`
    );
    onOpenCloseConfirm();
  };

  const onActivateDesactivate = async () => {
    try {
      await userController.updateUser(accessToken, user._id, {
        active: !user.active,
      });
      onReload();
      onOpenCloseConfirm();
    } catch (error) {
      console.error(error);
    }
  };

  const openDeleteConfirm = () => {
    setIsDelete(true);
    setConfirmHeader("Eliminar");
    setConfirmMessage(`¿Está seguro de eliminar el usuario "${user.email}"?`);
    onOpenCloseConfirm();
  };

  const onDelete = async () => {
    try {
      await userController.deleteUser(accessToken, user._id);
      onReload();
      onOpenCloseConfirm();
    } catch (error) {
      console.error(error);
    }
  };

  const can = (action) => {
  return (
    isMaster(role) ||
    isAdmin(role) ||
    hasPermission(permissionsByRole, role._id, "users", action)
  );
};


  return (
    <>
      <Table.Row key={user._id}>
        <Table.Cell data-label={t("name")}>
          {/* <input
            type="text"
            value={user.name}
            // onChange={e => handleEdit(item.id, 'name', e.target.value)}
          /> */}
          {user.firstname}
        </Table.Cell>
        <Table.Cell data-label={t("lastname")}>{user.lastname}</Table.Cell>
        <Table.Cell data-label={t("email")}>{user.email ? user.email : ""}</Table.Cell>
        {showCompanyColumn ? (
          <Table.Cell data-label={t("company")}>{user.company ? user.company.name : ""}</Table.Cell>
        ) : null}
        {showSiteColumn && (isMaster(role) || sites?.length !== 1) ? (
          <Table.Cell data-label={t("site")}>{(user.sites || []).map((s) => s?.name).filter(Boolean).join(", ")}</Table.Cell>
        ) : null}
        <Table.Cell data-label={t("role")}>{user.role ? user.role.name : ""}</Table.Cell>
        <Table.Cell data-label={t("actions")} className="users-table__actions" onClick={(e) => e.stopPropagation()}>
          <div className="user-item__row-menu">
            <button type="button" className="user-item__row-menu-btn" onClick={toggleRowMenu} aria-label={t("actions")}>
              <Icon name="ellipsis vertical" />
            </button>
          </div>
          {rowMenu && ReactDOM.createPortal(
            <div
              className="user-item__row-menu-list"
              style={{
                position: "fixed",
                top: rowMenu.top,
                left: rowMenu.left,
                transform: rowMenu.openUp ? "translateY(-100%)" : "none",
              }}
            >
              {can("edit") && (
                <button onClick={() => { openUpdateUser(); setRowMenu(null); }}>
                  <Icon name="pencil" />{t("edit")}
                </button>
              )}
              {can("edit") && !hideActivate && (
                <button onClick={() => { openDesactivateActivateConfim(); setRowMenu(null); }}>
                  <Icon name={user.active ? "ban" : "check"} />{user.active ? t("deactivate") : t("activate")}
                </button>
              )}
              {can("delete") && (
                <button className="user-item__row-menu-item--danger" onClick={() => { openDeleteConfirm(); setRowMenu(null); }}>
                  <Icon name="trash" />{t("delete")}
                </button>
              )}
            </div>,
            document.body
          )}
        </Table.Cell>
      </Table.Row>

      <BasicModal show={showModal} close={onOpenCloseModal} title={titleModal} size="small">
        <UserForm close={onOpenCloseModal} onReload={onReload} user={user} />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={confirmHeader}
        message={confirmMessage}
        onCancel={onOpenCloseConfirm}
        onConfirm={isDelete ? onDelete : isActivationError ? onOpenCloseConfirm : onActivateDesactivate}
        confirmLabel="Aceptar"
        cancelLabel={isActivationError ? null : t("cancel")}
        confirmDanger={isDelete}
      />
    </>
  );
}

// <>
// <div className="user-item">
//   <div className="user-item__info">
//     {/* <Image
//       avatar
//       src={
//         user.avatar ? `${ENV.BASE_PATH}/${user.avatar}` : image.noAvatar
//       }
//     /> */}
//     <div>
//       <p>
//         {user.firstname} {user.lastname}
//       </p>
//       <p>{user.email}</p>
//     </div>
//   </div>

//   <div>
//     {(isMaster(role) || isAdmin(role) || hasPermission(permissionsByRole, role._id, "users", "edit"))?(
//     <Button icon primary onClick={openUpdateUser}>
//       <Icon name="pencil" />
//     </Button>) : null}
//     {(isMaster(role) || isAdmin(role) || hasPermission(permissionsByRole, role._id, "users", "edit"))?(
//     <Button
//       icon
//       color={user.active ? "orange" : "teal"}
//       onClick={openDesactivateActivateConfim}
//     >
//       <Icon name={user.active ? "ban" : "check"} />
//     </Button> ):null}
//     {(isMaster(role) || isAdmin(role) || hasPermission(permissionsByRole, role._id, "users", "delete"))?(
//     <Button icon color="red" onClick={openDeleteConfirm}>
//       <Icon name="trash" />
//     </Button> ) : null }
//   </div>
// </div>

// <BasicModal show={showModal} close={onOpenCloseModal} title={titleModal}>
//   <UserForm close={onOpenCloseModal} onReload={onReload} user={user} />
// </BasicModal>

// <Confirm
//   open={showConfirm}
//   onCancel={onOpenCloseConfirm}
//   onConfirm={isDelete ? onDelete : onActivateDesactivate}
//   content={confirmMessage}
//   size="tiny"
// />
// </>
